import { z } from 'zod';

/**
 * Environment variable schema with Zod validation
 * All variables must be prefixed with VITE_ to be exposed to the client
 */
const envSchema = z.object({
  // API Configuration
  VITE_API_URL: z.string().default(''), // Empty string for development (uses proxy paths directly)
  VITE_DEFAULT_API_URL: z
    .string()
    .url('VITE_DEFAULT_API_URL must be a valid URL')
    .default('https://api-admin.dev.keystonelabs.com.au'),

  // Azure AD / Microsoft Entra ID Configuration
  VITE_AZURE_CLIENT_ID: z
    .string()
    .min(1, 'VITE_AZURE_CLIENT_ID is required')
    .default(''),
  VITE_AZURE_TENANT_ID: z
    .string()
    .min(1, 'VITE_AZURE_TENANT_ID is required')
    .default(''),
  VITE_AUTH_REDIRECT_URI: z
    .string()
    .url('VITE_AUTH_REDIRECT_URI must be a valid URL')
    .default('http://localhost:5173/auth/callback'),

  // App Configuration
  VITE_APP_NAME: z
    .string()
    .min(1, 'VITE_APP_NAME is required')
    .default('MyPayNow Support'),

  VITE_ENVIRONMENT: z.enum(['dev', 'staging', 'production']).default('dev'),

  // E2E ONLY. When 'true', the route guards also accept a stored MPN session
  // (written by e2e/auth.setup.ts from the dev backend's god token) — the
  // local Playwright lane, since localhost cannot complete an MSAL login.
  // playwright.config.ts sets it on the Vite process it starts; nothing else
  // should. Refused outright in production builds (see validateEnv).
  VITE_E2E_AUTH_BYPASS: z
    .string()
    .default('false')
    .transform((val: string) => val === 'true'),

  // Feature Flags
  VITE_ENABLE_ANALYTICS: z
    .string()
    .default('false')
    .transform((val: string) => val === 'true'),

  VITE_ENABLE_DEVTOOLS: z
    .string()
    .default('true')
    .transform((val: string) => val === 'true'),

  // Waive/Hold Fee(s) on the advance ledger (MPN-113). Off unless the build
  // says 'true', so a deploy that never sets it ships without the button: dev
  // and staging turn it on through their GitHub Environment variable,
  // production stays off until the feature is cleared for release.
  VITE_ENABLE_WAIVE_FEES: z
    .string()
    .default('false')
    .transform((val: string) => val === 'true'),

  // Record a missed payment from the advance ledger's "Add" (MPN-102). Off
  // unless the build says 'true': the backend route is on dev only, so dev
  // turns it on through its GitHub Environment variable and staging and
  // production stay off until the backend is promoted.
  VITE_ENABLE_RECORD_PAYMENT: z
    .string()
    .default('false')
    .transform((val: string) => val === 'true'),

  // Performance
  // Client-side abort for a single request. Two minutes is deliberately far
  // above API Gateway's ~30s integration ceiling: the gateway / backend should
  // be the ones to time out (502/504) so the UI surfaces the server's error
  // rather than a client abort. Deployed builds take this from the GitHub
  // Environment variable of the same name (see cicd-app-deployment.yaml).
  VITE_API_TIMEOUT: z
    .string()
    .default('120000')
    .transform((val: string) => parseInt(val, 10))
    .refine(
      (val: number) => !isNaN(val) && val > 0,
      'VITE_API_TIMEOUT must be a positive number'
    ),

  // External Services
  VITE_SENTRY_DSN: z
    .string()
    .url('VITE_SENTRY_DSN must be a valid URL')
    .optional()
    .or(z.literal('')),

  // Proxy Endpoints (for development)
  VITE_PROXY_API_GATEWAY: z.string().optional().or(z.literal('')),

  // Security - Origin verification secret
  VITE_ORIGIN_VERIFY_SECRET: z.string().optional().or(z.literal('')),

  // Zendesk group that scopes the Tickets tab to this environment. All three
  // environments share ONE production Zendesk workspace (no sandbox), and the
  // backend routes tickets it creates into a per-environment group. Set this
  // to that group's id (dev/staging share one, production has its own) so the
  // tab only searches tickets raised against this environment. Leave empty to
  // search the whole workspace. Group ids are not secret — they appear in
  // Zendesk URLs.
  VITE_ZENDESK_GROUP_ID: z
    .string()
    .regex(/^\d*$/, 'VITE_ZENDESK_GROUP_ID must be a numeric Zendesk group id')
    .default(''),

  // Non-prod admin API (QA/UAT automated actions). Never set in production.
  VITE_NON_PROD_ADMIN_API_URL: z
    .string()
    .url('VITE_NON_PROD_ADMIN_API_URL must be a valid URL')
    .optional()
    .or(z.literal('')),

  // Power BI Embedded (POC)
  // Which embedding model to use for token acquisition:
  // - 'user-owns-data': acquire an AAD token client-side via MSAL (no backend,
  //   but each user needs Power BI access). Current POC default.
  // - 'app-owns-data': fetch an embed token from the backend (no per-user
  //   Power BI licence, but requires a server endpoint to mint the token).
  VITE_POWERBI_EMBED_MODE: z
    .enum(['user-owns-data', 'app-owns-data'])
    .default('user-owns-data'),

  // Power BI workspace (group) ID. Every report the signed-in user can see in
  // it becomes a Reports menu item + page (listed at runtime, no per-report
  // config). Empty = the Reports section shows "not configured".
  VITE_POWERBI_GROUP_ID: z.string().optional().or(z.literal('')),
});

function validateEnv() {
  try {
    const parsed = envSchema.parse(import.meta.env);
    if (
      parsed.VITE_E2E_AUTH_BYPASS &&
      parsed.VITE_ENVIRONMENT === 'production'
    ) {
      throw new Error(
        'Environment validation failed:\nVITE_E2E_AUTH_BYPASS must not be enabled in a production build'
      );
    }
    return parsed;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.issues.map(
        err => `${err.path.join('.')}: ${err.message}`
      );
      throw new Error(
        `Environment validation failed:\n${missingVars.join('\n')}`
      );
    }
    throw error;
  }
}

export const env = validateEnv();

// Type for environment variables
export type Environment = z.infer<typeof envSchema>;

// Helper functions
export function isDevelopment(): boolean {
  return env.VITE_ENVIRONMENT === 'dev';
}

export function isProduction(): boolean {
  return env.VITE_ENVIRONMENT === 'production';
}

export function isStaging(): boolean {
  return env.VITE_ENVIRONMENT === 'staging';
}

export function isNonProdActionsEnabled(): boolean {
  return !isProduction() && !!env.VITE_NON_PROD_ADMIN_API_URL;
}

/**
 * Waive/Hold Fee(s) (MPN-113) ships behind VITE_ENABLE_WAIVE_FEES: the
 * ledger's "Waive fees" button and its modal only render when the build was
 * given 'true'. Read-side rendering (Charge Waiver rows, audit log entries)
 * is not gated — those describe what the backend already did.
 */
export function isWaiveFeesEnabled(): boolean {
  return env.VITE_ENABLE_WAIVE_FEES;
}

/**
 * Record a missed payment (MPN-102) ships behind VITE_ENABLE_RECORD_PAYMENT:
 * without it the ledger's "Add" stays the disabled placeholder it always was.
 * The resulting Repayment rows and audit log entries are not gated.
 */
export function isRecordPaymentEnabled(): boolean {
  return env.VITE_ENABLE_RECORD_PAYMENT;
}

/**
 * The Zendesk group id the Tickets tab is scoped to, or `null` for the whole
 * workspace. Applied in every environment, production included: the backend
 * stamps the same per-environment group on the tickets it creates, so the
 * variable must name the group this environment's tickets actually land in.
 */
export function zendeskGroupFilterId(): string | null {
  return env.VITE_ZENDESK_GROUP_ID || null;
}

/**
 * E2E-only escape hatch for the MSAL route guards (see
 * src/components/auth/e2eAuthBypass.ts). Only true when the Vite process was
 * started with VITE_E2E_AUTH_BYPASS=true outside production.
 */
export function isE2eAuthBypassEnabled(): boolean {
  return env.VITE_E2E_AUTH_BYPASS && !isProduction();
}

/**
 * The Reports section is configured once a workspace ID is present. It lists
 * the workspace as the signed-in user, which only the user-owns-data model can
 * do — app-owns-data would need a backend listing endpoint that doesn't exist.
 */
export function isPowerBiConfigured(): boolean {
  return (
    env.VITE_POWERBI_EMBED_MODE === 'user-owns-data' &&
    !!env.VITE_POWERBI_GROUP_ID
  );
}
